import { Row, Col, FormGroup, Label, Input, FormFeedback, Button, Alert } from "reactstrap";
import { Field, FieldArray } from "formik";
import FeatherIcon from "feather-icons-react";
import PropTypes from "prop-types";

// Column configs for the three resident lists General Information renders. The
// Name / Email / Contact columns share widths across all three so the sections
// line up; the remaining columns (Birthday, Age, Relation) take what is left.
// `md` widths per row must sum to 11 across the four or five columns — the
// 12th column holds the add/remove buttons.
//
// A column may carry `toggleKey`: a checkbox rendered under the input and bound
// to that key on the same row. It defaults to checked when the row has no value
// for the key, so an existing record reads as "on" without being rewritten.
export const LEASE_HOLDER_COLUMNS = [
  { key: "name", label: "Lease Holder Name", md: 3, placeholder: "Enter name", required: true },
  { key: "email", label: "Email", md: 3, placeholder: "Enter email" },
  { key: "phone", label: "Contact", md: 3, placeholder: "Enter contact number" },
  { key: "birthday", label: "Birthday", md: 2, type: "date", toggleKey: "greet", toggleLabel: "Wish" },
];
export const EMPTY_LEASE_HOLDER = { name: "", email: "", phone: "", birthday: "", greet: true };

// Occupants keep their stricter rules (name, contact and email required, numeric age).
const parseAge = (raw) => (raw === "" || raw === null || raw === undefined ? null : parseInt(raw, 10));
export const OCCUPANT_COLUMNS = [
  { key: "name", label: "Occupant Name", md: 3, placeholder: "Enter name", required: true },
  { key: "email", label: "Email", md: 2, placeholder: "Enter email", required: true },
  { key: "phone", label: "Contact", md: 2, placeholder: "Enter contact number", required: true },
  { key: "birthday", label: "Birthday", md: 2, type: "date", toggleKey: "greet", toggleLabel: "Wish" },
  // Age stays at md:2. At the md breakpoint the tenant form's modal is only
  // 500px wide, and md:1 leaves the number input no room at all for a
  // two-digit age; Email gives up the unit instead.
  { key: "age", label: "Age", md: 2, placeholder: "Age", type: "number", parse: parseAge },
];
export const EMPTY_OCCUPANT = { name: "", phone: "", email: "", age: null, birthday: "", greet: true };

// Emergency contacts are untouched: an emergency contact is not a resident and
// is never greeted, so no birthday column here.
export const EMERGENCY_CONTACT_COLUMNS = [
  { key: "name", label: "Emergency Contact Name", md: 4, placeholder: "Enter name", required: true },
  { key: "email", label: "Email", md: 3, placeholder: "Enter email" },
  { key: "phone", label: "Contact", md: 2, placeholder: "Enter contact number" },
  { key: "relation", label: "Relation", md: 2, placeholder: "e.g. Spouse, Parent" },
];
export const EMPTY_EMERGENCY_CONTACT = { name: "", email: "", phone: "", relation: "" };

/**
 * Generic Formik repeater for a list of contact rows under `meta.*`. Optional by
 * design: an empty list renders just an "Add" button, no N/A placeholder row.
 * The parent supplies `getNestedError` so error/touched resolution matches the
 * rest of the form.
 */
const ContactRepeater = ({ name, title, icon = "users", singular, columns, emptyRow, max = 10, values, setFieldValue, getNestedError, headerExtra = null }) => {
  const rows = Array.isArray(values) ? values : [];
  const listError = getNestedError(name);

  return (
    <div className="mb-0" data-testid={`repeater-${name}`}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Label className="meta-repeater-title mb-0">
          <FeatherIcon icon={icon} size="13" className="me-2" />
          {title}
        </Label>
        <div className="d-flex align-items-center gap-3">
          {headerExtra}
          <span className="text-muted small">{rows.length}/{max}</span>
        </div>
      </div>
      {typeof listError === "string" && (
        <Alert color="danger" className="mb-2">{listError}</Alert>
      )}
      <FieldArray name={name}>
        {({ push, remove }) => (
          <>
            {rows.map((_, index) => (
              <Row key={index} className="mb-3 align-items-end">
                {columns.map((col) => {
                  const path = `${name}[${index}].${col.key}`;
                  const err = getNestedError(path);
                  return (
                    // Two per row between 768px and 991px, the designed widths only from
                    // 992px up. The tenant form sits in a .modal-xl, which Bootstrap does not
                    // widen until 1200px, while md columns switch on at 768px — so packing five
                    // columns into a 444px row there clips every one of them to nothing.
                    <Col xs={12} md={6} lg={col.md} key={col.key}>
                      <FormGroup className="mb-0">
                        {/* The toggle sits on the label line, right-aligned, the same way
                            "Not Applicable?" does above Parking Type. Below the input it hung
                            past the bottom of the row and threw the whole line out of line. */}
                        {col.toggleKey ? (
                          <div className="d-flex align-items-center justify-content-between w-100 mb-2">
                            <Label className="form-label mb-0" htmlFor={path}>
                              {col.label}{col.required ? " *" : ""}
                            </Label>
                            <span className="d-flex align-items-center">
                              <Label className="form-label mb-0 me-2" htmlFor={`${name}-${index}-${col.toggleKey}`}>
                                {col.toggleLabel || "On"}
                              </Label>
                              <Input
                                type="checkbox"
                                id={`${name}-${index}-${col.toggleKey}`}
                                className="form-check-input mt-0"
                                checked={rows[index]?.[col.toggleKey] !== false}
                                onChange={(e) => setFieldValue(`${name}[${index}].${col.toggleKey}`, e.target.checked)}
                              />
                            </span>
                          </div>
                        ) : (
                          <Label htmlFor={path}>{col.label}{col.required ? " *" : ""}</Label>
                        )}
                        <Field
                          name={path}
                          id={path}
                          type={col.type || "text"}
                          as={Input}
                          invalid={!!err}
                          placeholder={col.placeholder}
                          onChange={(e) => setFieldValue(path, col.parse ? col.parse(e.target.value) : e.target.value)}
                        />
                        <FormFeedback>{err}</FormFeedback>
                      </FormGroup>
                    </Col>
                  );
                })}
                <Col xs={12} md={6} lg={1}>
                  <div className="d-flex align-items-center justify-content-end">
                    {index === rows.length - 1 && rows.length < max && (
                      <Button color="primary" size="sm" aria-label={`Add ${singular}`} onClick={() => push({ ...emptyRow })}>
                        <FeatherIcon icon="plus" size="16" />
                      </Button>
                    )}
                    <Button color="danger" size="sm" className="ms-1" aria-label={`Remove ${singular} ${index + 1}`} onClick={() => remove(index)}>
                      <FeatherIcon icon="x" size="16" />
                    </Button>
                  </div>
                </Col>
              </Row>
            ))}
            {rows.length === 0 && (
              <div className="text-center mb-2">
                <Button color="secondary" outline aria-label={`Add ${singular}`} onClick={() => push({ ...emptyRow })}>
                  Add {singular}
                </Button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

ContactRepeater.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  singular: PropTypes.string.isRequired,
  icon: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    md: PropTypes.number.isRequired,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    type: PropTypes.string,
    parse: PropTypes.func,
    toggleKey: PropTypes.string,
    toggleLabel: PropTypes.string,
  })).isRequired,
  emptyRow: PropTypes.object.isRequired,
  max: PropTypes.number,
  values: PropTypes.array,
  setFieldValue: PropTypes.func.isRequired,
  getNestedError: PropTypes.func.isRequired,
  headerExtra: PropTypes.node,
};

export default ContactRepeater;
